import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useQueryClient } from "@tanstack/react-query"

import type { DashboardDatePreferences } from "../widgets/types/dashboardConfig"
import {
  mapTransportDashboardMeta,
  serializeGetDashboardLayoutRequest,
  serializeUpdateDashboardRequest,
} from "../widgets/utils/transportDashboard"

/**
 * Hook to manage dashboard-specific date range preferences
 * Provides methods to save and clear date preferences stored in dashboard.config
 */
export const useDashboardDatePreferencesTransport = (
  dashboardId: string | undefined,
) => {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const { mutateAsync: updateDashboard, isPending: isUpdatingDashboard } =
    useTransportMutation("updateDashboard")

  const transportDashboardsQuery = useTransportQuery({
    operation: "listDashboardMetadata",
    options: {
      ...CACHE_PROFILES.lookup,
    },
  })
  const dashboards = useMemo(
    () =>
      transportDashboardsQuery.data?.dashboards.map((dashboard) =>
        mapTransportDashboardMeta(dashboard),
      ) ?? [],
    [transportDashboardsQuery.data?.dashboards],
  )
  const currentDashboard = useMemo(
    () => dashboards?.find((d) => d.id === dashboardId),
    [dashboards, dashboardId],
  )

  // Extract date preferences from dashboard config
  const savedPreferences = useMemo(
    () => currentDashboard?.config?.datePreferences,
    [currentDashboard],
  )

  const invalidateTransportDashboardQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: transportQueryUtils.getKey("listDashboardMetadata"),
    })

    if (!dashboardId) {
      return
    }

    void queryClient.invalidateQueries({
      queryKey: transportQueryUtils.getKey(
        "getDashboardLayout",
        serializeGetDashboardLayoutRequest(dashboardId),
      ),
    })
  }, [dashboardId, queryClient, transportQueryUtils])

  /**
   * Save date preference to dashboard config
   */
  const saveDatePreference = useCallback(
    async (preferences: DashboardDatePreferences) => {
      if (!dashboardId) {
        return
      }

      await updateDashboard(
        serializeUpdateDashboardRequest({
          dashboardId,
          datePreferences: {
            preset: preferences.preset,
            customRange: preferences.customRange,
            relativeConfig: preferences.relativeConfig,
          },
        }),
      )
      invalidateTransportDashboardQueries()
    },
    [dashboardId, invalidateTransportDashboardQueries, updateDashboard],
  )

  /**
   * Clear date preference (reverts to default last-7-days)
   */
  const clearDatePreference = useCallback(async () => {
    if (!dashboardId) {
      return
    }

    await updateDashboard(
      serializeUpdateDashboardRequest({
        dashboardId,
        datePreferences: null,
      }),
    )
    invalidateTransportDashboardQueries()
  }, [dashboardId, invalidateTransportDashboardQueries, updateDashboard])

  return {
    savedPreferences,
    saveDatePreference,
    clearDatePreference,
    isSaving: isUpdatingDashboard,
  }
}
